import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, AlertCircle, Loader2 } from 'lucide-react';
import { FREQUENCY_OPTIONS, getMedication, updateMedication, deleteMedication } from '@/lib/medications';

export default function MedicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [med, setMed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('once daily');
  const [scheduleTimes, setScheduleTimes] = useState([]);
  const [pendingTime, setPendingTime] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const m = await getMedication(id);
      setMed(m); setName(m.name); setDosage(m.dosage); setFrequency(m.frequency); setScheduleTimes(m.schedule_times || []); setNotes(m.notes || '');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addTime = () => {
    const t = pendingTime.trim();
    if (!t || !/^([01]\d|2[0-3]):[0-5]\d$/.test(t)) { setError('Time must be HH:MM format.'); return; }
    if (scheduleTimes.includes(t)) { setPendingTime(''); return; }
    setScheduleTimes([...scheduleTimes, t].sort()); setPendingTime(''); setError(null);
  };

  const onSave = async () => {
    if (!med) return;
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!dosage.trim()) { setError('Dosage is required.'); return; }
    setSubmitting(true);
    try {
      const updated = await updateMedication(med.id, { name: name.trim(), dosage: dosage.trim(), frequency, schedule_times: scheduleTimes, notes: notes.trim() || null });
      setMed(updated); setEditMode(false);
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  const confirmDelete = () => {
    if (!med) return;
    if (window.confirm(`Delete ${med.name}? This cannot be undone.`)) doDelete();
  };

  const doDelete = async () => {
    if (!med) return;
    setSubmitting(true);
    try { await deleteMedication(med.id); navigate('/medications', { replace: true }); } catch (e) { setError(e.message); setSubmitting(false); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D77A52' }} />
    </div>
  );

  if (!med) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center">
      <h2 className="text-xl font-extrabold mb-2" style={{ color: '#0F2A35' }}>Medication not found</h2>
      <p className="mb-4" style={{ color: '#4B6B79' }}>It may have been deleted.</p>
      <button onClick={() => navigate('/medications', { replace: true })} className="px-6 py-3 rounded-xl font-bold text-white" style={{ backgroundColor: '#D77A52' }}>Back to list</button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="medication-detail-screen">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-black/5"><ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} /></button>
        <h1 className="text-3xl font-extrabold truncate" style={{ color: '#5B2D1F' }} data-testid="medication-detail-name">{med.name}</h1>
      </div>

      {med.unverified && (
        <div className="flex items-center gap-2 p-3 rounded-xl border mb-4" style={{ backgroundColor: '#FFF4DF', borderColor: '#B07A1F' }}>
          <AlertCircle className="h-4 w-4" style={{ color: '#B07A1F' }} />
          <span className="text-sm font-semibold" style={{ color: '#B07A1F' }}>This medication is unverified — interaction checks may be limited.</span>
        </div>
      )}

      {error && <div data-testid="medication-detail-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}><p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p></div>}

      {!editMode ? (
        <div className="rounded-2xl border px-6 py-2 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
          {[
            { label: 'Dosage', value: med.dosage },
            { label: 'Frequency', value: med.frequency },
            ...(med.schedule_times.length > 0 ? [{ label: 'Schedule', value: med.schedule_times.join(', ') }] : []),
            ...(med.rxcui ? [{ label: 'RxCUI', value: med.rxcui }] : []),
            ...(med.barcode_ndc ? [{ label: 'NDC', value: med.barcode_ndc }] : []),
            ...(med.notes ? [{ label: 'Notes', value: med.notes }] : []),
          ].map((row, i) => (
            <div key={row.label} className={`py-3 ${i > 0 ? 'border-t' : ''}`} style={{ borderColor: '#D7E2E8' }}>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4B6B79' }}>{row.label}</span>
              <p className="text-base" style={{ color: '#0F2A35' }}>{row.value}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          <div>
            <label htmlFor="edit-name" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Name</label>
            <input id="edit-name" data-testid="medication-edit-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
          </div>
          <div>
            <label htmlFor="edit-dosage" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Dosage</label>
            <input id="edit-dosage" data-testid="medication-edit-dosage" value={dosage} onChange={(e) => setDosage(e.target.value)} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
          </div>
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: '#0F2A35' }}>Frequency</p>
            <div className="flex flex-wrap gap-2">
              {FREQUENCY_OPTIONS.map((opt) => (
                <button key={opt} type="button" onClick={() => setFrequency(opt)} data-testid={`medication-edit-freq-${opt.replace(/\s+/g, '-')}`} className={`px-4 py-2.5 rounded-full border-[1.5px] text-sm font-bold transition-colors ${frequency === opt ? 'text-white' : ''}`} style={frequency === opt ? { backgroundColor: '#D77A52', borderColor: '#D77A52', color: '#FFFFFF' } : { borderColor: '#D77A52', color: '#D77A52' }}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Schedule times</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {scheduleTimes.map((t) => (
                <button key={t} type="button" onClick={() => setScheduleTimes(scheduleTimes.filter((x) => x !== t))} data-testid={`medication-edit-time-${t}`} className="px-3 py-1.5 rounded-full text-sm font-bold" style={{ backgroundColor: '#EEF4F7', color: '#5B2D1F' }}>
                  {t} ✕
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={pendingTime} onChange={(e) => setPendingTime(e.target.value)} placeholder="HH:MM" data-testid="medication-edit-time-input" className="flex-1 px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
              <button type="button" onClick={addTime} data-testid="medication-edit-time-add" className="px-4 py-3 rounded-xl font-bold border-2" style={{ borderColor: '#D77A52', color: '#D77A52' }}>Add</button>
            </div>
          </div>
          <div>
            <label htmlFor="edit-notes" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Notes</label>
            <textarea id="edit-notes" data-testid="medication-edit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2 resize-none" style={{ borderColor: '#D7E2E8' }} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        {editMode ? (
          <>
            <button onClick={onSave} disabled={submitting} data-testid="medication-edit-save" className="w-full py-4 rounded-xl text-lg font-extrabold text-white disabled:opacity-50" style={{ backgroundColor: '#D77A52' }}>{submitting ? 'Saving…' : 'Save changes'}</button>
            <button onClick={() => { setEditMode(false); setError(null); setName(med.name); setDosage(med.dosage); setFrequency(med.frequency); setScheduleTimes(med.schedule_times || []); setNotes(med.notes || ''); }} data-testid="medication-edit-cancel" className="w-full py-4 rounded-xl text-lg font-bold border-2" style={{ borderColor: '#D77A52', color: '#D77A52' }}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditMode(true)} data-testid="medication-detail-edit-btn" className="w-full py-4 rounded-xl text-lg font-extrabold text-white" style={{ backgroundColor: '#D77A52' }}>Edit</button>
            <button onClick={confirmDelete} disabled={submitting} data-testid="medication-detail-delete-btn" className="w-full py-4 rounded-xl text-lg font-bold text-white disabled:opacity-50" style={{ backgroundColor: '#B0303A' }}>{submitting ? 'Deleting…' : 'Delete'}</button>
          </>
        )}
      </div>
    </div>
  );
}
